const includesAny = (text, keywords) => {
  const normalized = String(text || '').toLowerCase();
  return keywords.some((keyword) => normalized.includes(keyword));
};

const addAlert = (alerts, alert) => {
  if (alerts.some((current) => current.id === alert.id)) return alerts;
  return [...alerts, alert];
};

const hasUsefulValue = (value) => {
  const text = String(value || '').trim().toLowerCase();
  return text && !['no', 'ninguno', 'ninguna', 'niega', 'niega alergias conocidas', 'sin registro'].includes(text);
};

const chronicDiseaseKeywords = [
  ['diabetes', 'Diabetes mellitus'],
  ['hipertension', 'Hipertension arterial'],
  ['asma', 'Asma o enfermedad respiratoria cronica'],
  ['epoc', 'EPOC'],
  ['cardiopatia', 'Cardiopatia'],
  ['insuficiencia renal', 'Enfermedad renal cronica'],
  ['cancer', 'Cancer'],
  ['vih', 'VIH'],
  ['epilepsia', 'Epilepsia'],
  ['anticoagul', 'Uso o antecedente de anticoagulacion'],
];

const riskMedicationKeywords = [
  ['warfarina', 'Warfarina'],
  ['rivaroxaban', 'Rivaroxaban'],
  ['apixaban', 'Apixaban'],
  ['dabigatran', 'Dabigatran'],
  ['heparina', 'Heparina'],
  ['insulina', 'Insulina'],
  ['metformina', 'Metformina'],
  ['digoxina', 'Digoxina'],
  ['litio', 'Litio'],
  ['metotrexato', 'Metotrexato'],
  ['isotretinoina', 'Isotretinoina'],
  ['clonazepam', 'Benzodiacepina'],
  ['diazepam', 'Benzodiacepina'],
  ['alprazolam', 'Benzodiacepina'],
  ['morfina', 'Opioide'],
  ['tramadol', 'Opioide'],
  ['oxicodona', 'Opioide'],
  ['prednisona', 'Corticoide sistemico'],
];

export const clinicalAlertSeverityLabels = {
  critical: 'Critica',
  high: 'Alta',
  medium: 'Media',
  info: 'Informativa',
};

export const generateClinicalAlerts = ({ patient = {}, preTriage = null, hcuForm = {} } = {}) => {
  let alerts = [];
  const age = Number.parseInt(patient.age || hcuForm.facilityPatientData?.age, 10);
  const sex = String(patient.sex || hcuForm.facilityPatientData?.sex || '').toLowerCase();
  const priorityGroup = String(patient.priorityGroup || '').toLowerCase();
  const allergies = patient.allergies || '';
  const medicalHistory = [
    patient.medicalHistory,
    hcuForm.personalHistory?.clinicalDetails,
    preTriage?.relevantHistory,
  ].filter(Boolean).join(' ');
  const currentMedications = String(patient.currentMedications || '').trim();
  const symptomText = [
    preTriage?.mainSymptoms,
    preTriage?.evolutionTime,
    hcuForm.consultationReason?.description,
    hcuForm.currentIllness,
  ].filter(Boolean).join(' ');

  if (hasUsefulValue(allergies)) {
    alerts = addAlert(alerts, {
      id: 'allergies',
      type: 'Alergias',
      severity: 'critical',
      title: 'Alergias registradas',
      detail: allergies,
      action: 'Verificar antes de prescribir o administrar medicacion.',
    });
  }

  const pregnancyText = `${priorityGroup} ${medicalHistory} ${symptomText}`;
  if (sex.includes('femenino') && includesAny(pregnancyText, ['embarazo', 'gestante', 'gestacion', 'pregnancy'])) {
    alerts = addAlert(alerts, {
      id: 'pregnancy',
      type: 'Embarazo',
      severity: 'high',
      title: 'Posible embarazo o condicion obstetrica',
      detail: 'El perfil o pretriaje menciona embarazo/gestacion.',
      action: 'Evitar farmacos contraindicados y valorar signos obstetricos de alarma.',
    });
  }

  if (Number.isFinite(age) && age < 18) {
    alerts = addAlert(alerts, {
      id: 'pediatric-age',
      type: 'Edad',
      severity: age < 5 ? 'high' : 'medium',
      title: 'Paciente pediatrico',
      detail: `${age} ano(s).`,
      action: 'Ajustar dosis por peso/edad y confirmar responsable legal.',
    });
  }

  if (Number.isFinite(age) && age >= 65) {
    alerts = addAlert(alerts, {
      id: 'older-adult',
      type: 'Edad',
      severity: age >= 80 ? 'high' : 'medium',
      title: 'Adulto mayor',
      detail: `${age} ano(s).`,
      action: 'Revisar fragilidad, interacciones, caidas y dosis renal.',
    });
  }

  const chronicMatches = chronicDiseaseKeywords
    .filter(([keyword]) => includesAny(medicalHistory, [keyword]))
    .map(([, label]) => label);
  if (chronicMatches.length > 0) {
    alerts = addAlert(alerts, {
      id: 'chronic-diseases',
      type: 'Antecedentes',
      severity: 'medium',
      title: 'Enfermedades cronicas relevantes',
      detail: [...new Set(chronicMatches)].join(', '),
      action: 'Considerar comorbilidades al diagnosticar, prescribir y definir seguimiento.',
    });
  }

  const selectedAlarmSigns = preTriage?.selectedAlarmSigns || [];
  if (selectedAlarmSigns.length > 0 || preTriage?.priority === 'alta') {
    alerts = addAlert(alerts, {
      id: 'alarm-symptoms',
      type: 'Sintomas',
      severity: 'critical',
      title: 'Signos de alarma en pretriaje',
      detail: selectedAlarmSigns.length > 0
        ? selectedAlarmSigns.map((sign) => sign.label).join(', ')
        : 'Pretriaje clasificado como prioridad alta.',
      action: 'Valorar derivacion urgente o atencion presencial si corresponde.',
    });
  }

  if (includesAny(symptomText, ['dolor de pecho', 'disnea', 'dificultad respiratoria', 'desmayo', 'sangrado', 'convulsion', 'confusion'])) {
    alerts = addAlert(alerts, {
      id: 'danger-symptom-text',
      type: 'Sintomas',
      severity: 'high',
      title: 'Sintomas potencialmente graves',
      detail: 'El motivo o enfermedad actual contiene sintomas de alarma.',
      action: 'Confirmar severidad, inicio, signos vitales y criterios de emergencia.',
    });
  }

  const riskMedicationMatches = riskMedicationKeywords
    .filter(([keyword]) => includesAny(currentMedications, [keyword]))
    .map(([, label]) => label);
  if (riskMedicationMatches.length > 0) {
    alerts = addAlert(alerts, {
      id: 'risk-medications',
      type: 'Medicamentos',
      severity: 'high',
      title: 'Medicamentos de riesgo o alta vigilancia',
      detail: [...new Set(riskMedicationMatches)].join(', '),
      action: 'Revisar interacciones, duplicidades, contraindicaciones y ajustes de dosis.',
    });
  }

  if (hasUsefulValue(patient.disability) && String(patient.disability).toLowerCase() !== 'no') {
    alerts = addAlert(alerts, {
      id: 'disability',
      type: 'Prioritario',
      severity: 'info',
      title: 'Discapacidad registrada',
      detail: [patient.disability, patient.disabilityDetail].filter(Boolean).join(' '),
      action: 'Ajustar comunicacion, accesibilidad y plan de seguimiento.',
    });
  }

  const severityOrder = { critical: 0, high: 1, medium: 2, info: 3 };
  return alerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
};
